import {
  Canvas,
  IMPERIAL_SCALE_OPTIONS,
  LengthFormat,
  PageFormat,
  resolveLengthFormat,
  UnitSystem,
} from '@ritabygglov/core';
import { ChangeEvent, ChangeEventHandler, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { canvasService } from '../../apiServices/CanvasService';
import { siteConfig } from '../../config/siteConfig';
import { DEFAULT_SCALE, SCALE_OPTIONS } from '../../constants';
import { useModal } from '../../context/Modal/useModal';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import Button, { ButtonType } from '../Button/Button';
import { Flex } from '../Flex/Flex';
import { FormatOptions } from '../Input/FormatOptions';
import { LengthFormatOptions } from '../Input/LengthFormatOptions';
import { LocationIcon } from '../Icons';
import InputField, { SelectInput } from '../Input/Input';
import { PdfPagePicker } from '../PdfPagePicker/PdfPagePicker';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 32px;

  & a {
    text-decoration: none;
  }

  & a:visited {
    color: var(--active-blue);
  }
`;

const Item = styled.div`
  border: 1px solid var(--border-light);
  background-color: var(--surface-elevated);
  text-align: left;
  padding: 16px;
  gap: 8px;
  border-radius: 2px;
`;

const ItemHeader = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 550;
  color: var(--text);
`;

const Text = styled.p`
  margin-top: 4px;
  margin-bottom: 0.5rem;
`;

const FileNameContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 16px;
  padding: 2px 2px 2px 0px;
`;

const FileName = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 14px;
  font-weight: 500;
`;

const ExternalLink = styled(Link)`
  color: var(--active-blue);
  font-family: Inter;
  font-weight: 500;
  text-align: left;
  display: block;
  margin-bottom: 1rem;
`;

const CheckboxContainer = styled.div`
  padding: 0.2rem;
`;

const LengthFormatSelect = styled(SelectInput)`
  margin-left: 0.25rem;
`;

type SettingsProp = {
  canvasId: string;
};

const getFileName = (mapUrl?: string | null) => {
  const fileNameBits = mapUrl?.split('/');
  const fileName =
    fileNameBits &&
    fileNameBits.length > 0 &&
    fileNameBits[fileNameBits.length - 1];
  return fileName;
};

export const Settings = ({ canvasId }: SettingsProp) => {
  const { fetchUser, canvases } = useUser();
  const canvas = canvases.find((c) => c.canvasId === canvasId);
  const [scale, setScale] = useState(canvas?.drawingHeadScale || DEFAULT_SCALE);
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(
    canvas?.unitSystem || 'metric'
  );
  const [lengthFormat, setLengthFormat] = useState<LengthFormat>(
    resolveLengthFormat(
      canvas?.unitSystem || 'metric',
      canvas?.lengthFormat || 'meters'
    )
  );
  const toast = useToast();
  const intl = useIntl();
  const modal = useModal();
  const [isUploading, setIsUploading] = useState(false);

  if (!canvas) return null;

  const fileName = getFileName(canvas.mapUrl);

  const activeScaleOptions =
    unitSystem === 'imperial'
      ? IMPERIAL_SCALE_OPTIONS.map((option) => ({
          value: option.ratio,
          label: option.label,
        }))
      : SCALE_OPTIONS.map((option) => ({ value: option, label: option }));
  // Keep the drawing's current scale selectable even if it isn't one of the
  // active unit system's presets, rather than silently snapping to a
  // different ratio when the unit system changes.
  const scaleOptions = activeScaleOptions.some(
    (option) => option.value === scale
  )
    ? activeScaleOptions
    : [{ value: scale, label: scale }, ...activeScaleOptions];

  const onChangeScale: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newScale = e.currentTarget.value;
    setScale(newScale);
    canvasService
      .editCanvas(canvas.canvasId, { drawingHeadScale: newScale })
      .then(({ message }) => {
        fetchUser();
        toast.addToast({
          message,
          type: 'success',
        });
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const onChangeUnitSystem: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newUnitSystem = e.currentTarget.value as UnitSystem;
    // Resolve the format to one the new unit system can render immediately,
    // both in the select's own state and what gets persisted — otherwise the
    // length-format dropdown would render blank (its value matches no
    // <option>) until the user separately picks a format.
    const resolvedLengthFormat = resolveLengthFormat(
      newUnitSystem,
      lengthFormat
    );
    setUnitSystem(newUnitSystem);
    setLengthFormat(resolvedLengthFormat);
    canvasService
      .editCanvas(canvas.canvasId, {
        unitSystem: newUnitSystem,
        lengthFormat: resolvedLengthFormat,
      })
      .then(({ message }) => {
        fetchUser();
        toast.addToast({
          message,
          type: 'success',
        });
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const onChangeLengthFormat: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newLengthFormat = e.currentTarget.value as LengthFormat;
    setLengthFormat(newLengthFormat);
    canvasService
      .editCanvas(canvas.canvasId, { lengthFormat: newLengthFormat })
      .then(({ message }) => {
        fetchUser();
        toast.addToast({
          message,
          type: 'success',
        });
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const onChangeFormat: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const newFormat = e.currentTarget.value as PageFormat;
    canvasService
      .editCanvas(canvas.canvasId, { pageFormat: newFormat })
      .then(({ message }) => {
        fetchUser();
        toast.addToast({
          message,
          type: 'success',
        });
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const uploadFile = (
    file: Blob,
    fileName: string,
    page?: number,
    rotation?: number
  ): Promise<void> => {
    setIsUploading(true);
    return canvasService
      .uploadMap(canvas.canvasId, file, fileName, page, rotation)
      .then(() => {
        toast.addToast({
          message: intl.formatMessage({ id: 'settings.uploadSuccess' }),
          type: 'success',
        });
        fetchUser();
      })
      .catch(() => {
        toast.addToast({
          message: intl.formatMessage({ id: 'settings.uploadError' }),
          type: 'error',
        });
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  const onUploadMap = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : undefined;
    if (!file) return;

    if (file.type === 'application/pdf') {
      modal.onOpen(
        <PdfPagePicker
          file={file}
          onClose={modal.onClose}
          onConfirm={(page, rotation) =>
            uploadFile(file, file.name, page, rotation)
          }
        />
      );
      return;
    }

    uploadFile(file, file.name);
  };

  const onDeleteMap = () => {
    canvasService
      .editCanvas(canvas.canvasId, { mapUrl: '' })
      .then(({ message }) => {
        toast.addToast({
          message,
          type: 'success',
        });
        fetchUser();
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const onToggleShowNorthArrow = (showNorthArrow: boolean) => {
    const form: Partial<Canvas> = {
      showNorthArrow,
    };

    canvasService
      .editCanvas(canvasId, form)
      .then(({ message }) => {
        toast.addToast({
          message,
          type: 'success',
        });
        fetchUser();
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };

  const onToggleShowDrawingHead = (showDrawingHead: boolean) => {
    const form: Partial<Canvas> = {
      showDrawingHead,
    };
    canvasService
      .editCanvas(canvasId, form)
      .then(({ message }) => {
        toast.addToast({
          message,
          type: 'success',
        });
        fetchUser();
      })
      .catch((e: Error) => {
        toast.addToast({
          message: e.message,
          type: 'error',
        });
      });
  };
  return (
    <SettingsContainer>
      {Boolean(canvas.mapUrl) === false && (
        <Item key="import">
          <ItemHeader>
            {intl.formatMessage({ id: 'settings.importMapTitle' })}
          </ItemHeader>
          <Text>
            {intl.formatMessage({ id: 'settings.importMapText' })}
            <ExternalLink
              target="_blank"
              to={siteConfig.guideLinks.importBackground}
            >
              {intl.formatMessage({ id: 'settings.importMapReadMore' })}
            </ExternalLink>
          </Text>
          <Flex $alignItems="end" $justifyContent="start">
            <InputField
              onChange={onUploadMap}
              label=" "
              type="file"
              id="map-upload"
              isLoading={isUploading}
            />
          </Flex>
        </Item>
      )}

      {Boolean(canvas.mapUrl) && (
        <Item key="map">
          <ItemHeader>
            <LocationIcon />{' '}
            {intl.formatMessage({ id: 'settings.importMapTitle' })}
          </ItemHeader>
          <Text>
            {intl.formatMessage({ id: 'settings.importMapText' })}
            <ExternalLink target="_blank" to={siteConfig.guideLinks.overview}>
              {intl.formatMessage({ id: 'settings.importMapReadMore' })}
            </ExternalLink>
          </Text>
          <FileNameContainer>
            <FileName>
              {intl.formatMessage(
                { id: 'settings.currentFile' },
                { filename: fileName }
              )}
            </FileName>
            <Button onClick={onDeleteMap} type={ButtonType.DangerLink}>
              {intl.formatMessage({ id: 'settings.deleteFile' })}
            </Button>
          </FileNameContainer>
        </Item>
      )}

      <Item key="units">
        <ItemHeader>
          {intl.formatMessage({ id: 'settings.unitSystemTitle' })}
        </ItemHeader>
        <Text>{intl.formatMessage({ id: 'settings.unitSystemText' })}</Text>
        <SelectInput
          value={unitSystem}
          onChange={onChangeUnitSystem}
          name="unitSystem"
          id="unitSystem"
          required
        >
          <option value="metric">
            {intl.formatMessage({ id: 'settings.unitSystemMetric' })}
          </option>
          <option value="imperial">
            {intl.formatMessage({ id: 'settings.unitSystemImperial' })}
          </option>
        </SelectInput>
        <LengthFormatSelect
          value={lengthFormat}
          onChange={onChangeLengthFormat}
          name="lengthFormat"
          id="lengthFormat"
          required
        >
          <LengthFormatOptions unitSystem={unitSystem} />
        </LengthFormatSelect>
      </Item>
      <Item key="scale">
        <ItemHeader>
          {intl.formatMessage({ id: 'settings.scaleTitle' })}
        </ItemHeader>
        <Text>
          {intl.formatMessage({ id: 'settings.scaleText' })}
          <ExternalLink target="_blank" to={siteConfig.guideLinks.scale}>
            {intl.formatMessage({ id: 'settings.scaleReadMore' })}
          </ExternalLink>
        </Text>
        <SelectInput
          value={scale}
          onChange={onChangeScale}
          name="scale"
          id="scale"
          required
        >
          {scaleOptions.map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </SelectInput>
      </Item>
      <Item key="format">
        <ItemHeader>
          {intl.formatMessage({ id: 'settings.formatTitle' })}
        </ItemHeader>
        <Text>{intl.formatMessage({ id: 'settings.formatText' })}</Text>
        <SelectInput
          defaultValue={canvas?.pageFormat}
          onChange={onChangeFormat}
          name="pageformat"
          id="pageformat"
          required
        >
          <FormatOptions />
        </SelectInput>
      </Item>
      <Item key="Titleblock">
        <ItemHeader>
          {intl.formatMessage({ id: 'settings.titleBlockSectionTitle' })}
        </ItemHeader>
        <Text>
          {intl.formatMessage({ id: 'settings.titleBlockSectionText' })}
        </Text>
        <CheckboxContainer>
          <label htmlFor={'northarrow'}>
            {intl.formatMessage({ id: 'settings.northArrowLabel' })}
          </label>
          <input
            type="checkbox"
            id={'northarrow'}
            name="northarrow"
            defaultChecked={canvas.showNorthArrow}
            onChange={(e) => onToggleShowNorthArrow(e.target.checked)}
          />
        </CheckboxContainer>
        <CheckboxContainer>
          <label htmlFor={'titleblock'}>
            {intl.formatMessage({ id: 'settings.titleBlockLabel' })}
          </label>
          <input
            type="checkbox"
            id={'titleblock'}
            name="titleblock"
            defaultChecked={canvas.showDrawingHead}
            onChange={(e) => onToggleShowDrawingHead(e.target.checked)}
          />
        </CheckboxContainer>
      </Item>
    </SettingsContainer>
  );
};
